/**
 * round10/debt-sweep — closes DEBT.md's round9/embed-config #2: a saved
 * TEMPLATE can use a tool or option the builder has since turned off on the
 * Embed page, and every homeowner who starts from it then hits the server's
 * 422 TOOL_DISABLED / CATALOG_VIOLATION at submit (structural values; the
 * embed coerces cosmetic ones on load). Nothing in the Portal said so.
 *
 * This is the Portal-side check the backend's own ledger asked for: run the
 * same two core predicates the submit handler runs (`disabledToolsInDesign`,
 * `catalogViolationsInDesign`) over each template's design, resolved
 * against the tenant's stored configuration exactly the way `/api/config`
 * resolves it. The Portal already imports core at runtime for exports and
 * estimates, so this adds no dependency.
 *
 * Pure and defensive: an unhydratable design (a future schema version, a
 * corrupt row) reports no conflicts rather than throwing — a warning that
 * crashes the Templates page would be worse than the missing warning.
 */
import {
  loadPresetIntoState,
  resolveToolOptions,
  resolveCatalogOverrides,
  disabledToolsInDesign,
  catalogViolationsInDesign,
  DESIGNER_TOOLS,
  OPTION_CATEGORIES,
} from "@dockpro/core";
import { resolveBayCatalog } from "@dockpro/core/catalogs";
import type { TenantConfig } from "./api";

export interface TemplateConflicts {
  /** Builder-facing labels of tools the design uses that are turned off ("Roofs"). */
  tools: string[];
  /** Builder-facing "Category: value" lines for options the design uses that
   * are not offered ("Decking: Composite (ThruFlow)"). */
  options: string[];
}

type TenantSlice = Pick<TenantConfig, "toolOptions" | "catalogOverrides" | "customBoatBays" | "entitlements">;

const NONE: TemplateConflicts = { tools: [], options: [] };

function toolLabel(key: string): string {
  const tools = DESIGNER_TOOLS as unknown as Record<string, { label?: string }>;
  return tools[key]?.label ?? key;
}

function optionLine(category: string, key: string | number, customBoatBays: unknown): string {
  const categories = OPTION_CATEGORIES as unknown as Record<string, {
    label?: string;
    options?: (customBays?: unknown) => Array<{ key: string | number; label: string }>;
  }>;
  const meta = categories[category];
  const label = meta?.label ?? category;
  let valueLabel = String(key);
  try {
    const found = meta?.options?.(customBoatBays)?.find((o) => o.key === key);
    if (found) valueLabel = found.label;
  } catch {
    // A category whose option list needs a catalog we couldn't resolve — the
    // raw key is still an honest thing to show.
  }
  return `${label}: ${valueLabel}`;
}

/**
 * Conflicts between one template's design and the tenant's current designer
 * configuration. Empty arrays mean "every homeowner starting from this
 * template can submit it".
 */
export function templateConflicts(designState: unknown, tenant: TenantSlice): TemplateConflicts {
  const customBoatBays = tenant.customBoatBays ?? undefined;
  let hydrated: unknown;
  let tools: Record<string, boolean>;
  let catalog: Record<string, (string | number)[]>;
  try {
    hydrated = loadPresetIntoState(designState as never, { bayCatalog: resolveBayCatalog(customBoatBays as never) });
    if (!hydrated) return NONE;
    tools = resolveToolOptions(tenant.toolOptions ?? undefined, tenant.entitlements?.editScope ?? "full") as Record<string, boolean>;
    catalog = resolveCatalogOverrides(tenant.catalogOverrides ?? undefined, { customBoatBays: customBoatBays as never }) as Record<string, (string | number)[]>;
  } catch {
    return NONE;
  }

  let disabled: string[];
  let violations: Array<{ category: string; key: string | number }>;
  try {
    disabled = disabledToolsInDesign(hydrated as never, tools as never) as string[];
    violations = catalogViolationsInDesign(hydrated as never, catalog as never) as Array<{ category: string; key: string | number }>;
  } catch {
    return NONE;
  }

  const optionLines = Array.from(
    new Set(violations.map((v) => optionLine(v.category, v.key, customBoatBays))),
  );
  return {
    tools: Array.from(new Set(disabled.map(toolLabel))),
    options: optionLines,
  };
}

export function hasConflicts(c: TemplateConflicts): boolean {
  return c.tools.length > 0 || c.options.length > 0;
}

/** One sentence for a template row: "Uses Roofs; Decking: Composite (ThruFlow)". */
export function describeConflicts(c: TemplateConflicts): string {
  const parts = [...c.tools, ...c.options];
  return parts.length ? `Uses ${parts.join("; ")}` : "";
}
